// --------------------------------------------------------------
// Created On: 2025-07-24
// Author: Dimitra Weinstein
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState } from "react";
import useReduxSelector from "src/hooks/useReduxSelector";
import { API } from "src/constants/miscellaneous";
import useApi from "src/hooks/useApi";
import apiRequest from "src/utilities/api/apiRequest";
import getApiError from "src/utilities/api/getApiError";
import Spinner from "src/components/Spinner/Spinner";
import HelpAsset from "src/components/HelpAsset/HelpAsset";
import HelpAssetgroup from "src/components/HelpAssetgroup/HelpAssetgroup";
import ConfirmModal from "src/components/ConfirmModal/ConfirmModal";
import ResourceList from "src/components/ResourceList/ResourceList";
import AssetgroupListRow from "src/pages/ManageAssetsPage/AssetgroupListRow/AssetgroupListRow";
import AssetListRow from "src/pages/ManageAssetsPage/AssetListRow/AssetListRow";
import delayMilliseconds from "src/utilities/delayMilliseconds";
import Error500Page from "src/pages/Error500Page/Error500Page";
import styles from "./AssetBulkConfigurationCompanyList.module.scss";

// List of companies that allows a super admin user to configure the selected company's assets and asset groups.
export default function AssetBulkConfigurationCompanyList(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [assetgroupAssetsLoading, setAssetgroupAssetsLoading] = useState<boolean>(false);
  const [failedToLoad, setFailedToLoad] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string>("");
  const [selectedType, setSelectedType] = useState<SelectedType>("");
  const [assetgroups, setAssetgroups] = useState<Assetgroup[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsToBeConfigured, setAssetsToBeConfigured] = useState<ConfigurableAsset[]>([]);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const [companyId, setCompanyId] = useState<number>(currentUser.companyId);

  // Get company assets and asset groups when a company is selected.
  useApi(
    () => {
      if (companyId !== -1 && selectedCompanyName !== "") {
        setLoading(true);
        return true;
      } else {
        setAssets([]);
        setAssetgroups([]);
        return false;
      }
    },
    {
      method: "GET",
      url: `${API}/company/${companyId}/assetgroup/map`,
      body: null,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        setAssets(responseBody.assets);
        setAssetgroups(responseBody.assetgroups);
        setFailedToLoad(false);
        setErrorMessage("");
      } else {
        setFailedToLoad(true);
      }
      setLoading(false);
    },
    [companyId]
  );

  // Fetch assets from the given assetgroup.
  useApi(
    () => {
      if (selectedId !== -1 && selectedType === "assetgroup") {
        setAssetgroupAssetsLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "POST",
      url: `${API}/company/${companyId}/assetgroup/asset`,
      body: { assetgroupIds: [selectedId] },
    },
    async (response: Response, responseBody: SelectedForConfigurationResponseBody) => {
      if (response.ok && responseBody) {
        setAssetsToBeConfigured(responseBody.assets);
      } else {
        setErrorMessage(await getApiError(response, "Failed to load assets for the selected asset group."));
      }
      setAssetgroupAssetsLoading(false);
    },
    [selectedId, selectedType, companyId, JSON.stringify(assets), JSON.stringify(assetgroups)]
  );

  // Change the currently selected target.
  function updateSelection(selectedId: number, selectedType: string): void {
    if (selectedType === "assetgroup") {
      const assetgroup = assetgroups.find((ag) => ag.assetgroupId === selectedId);
      if (assetgroup !== undefined) {
        setSelectedId(assetgroup.assetgroupId);
        setSelectedType("assetgroup");
      }
    } else if (selectedType === "asset") {
      const asset = assets.find((a) => a.assetId === selectedId);
      if (asset !== undefined) {
        setSelectedId(asset.assetId);
        setSelectedType("asset");
      }
    } else {
      setSelectedId(-1);
      setSelectedType("");
    }
  }

  // Sort assets.
  function sortAssets(assets: AssetListItem[]): AssetListItem[] {
    return assets.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  // Sort asset groups.
  function sortAssetgroups(assetgroups: Assetgroup[]): Assetgroup[] {
    return assetgroups.sort((a, b) => {
      const nameA = a.name.toUpperCase();
      const nameB = b.name.toUpperCase();
      if (a.isDefault) {
        return -1;
      } else if (b.isDefault) {
        return 1;
      } else if (nameA < nameB) {
        return -1;
      } else if (nameA > nameB) {
        return 1;
      } else {
        return 0;
      }
    });
  }

  // Given a company name, gets its ID.
  function getCompanyIdByName(companyName: string): number {
    let companyId = -1;
    const company = props.companies.find((company) => company.name === companyName);
    if (company !== undefined) {
      companyId = company.companyId;
    } else {
      companyId = -1;
    }
    return companyId;
  }

  // Given an ID, gets an asset group's name.
  function getAssetgroupName(assetgroupId: number): string {
    let name = "";
    const assetgroup = assetgroups.find((assetgroup) => assetgroup.assetgroupId === assetgroupId);
    if (assetgroup !== undefined) {
      name = assetgroup.name;
    }
    return name;
  }

  // Given an ID, gets an asset's name.
  function getAssetNameById(assetId: number): string {
    let name = "";
    const asset = assets.find((asset) => asset.assetId === assetId);
    if (asset !== undefined) {
      name = asset.name;
    }
    return name;
  }

  // Returns a comma-separated list of asset names within an asset group.
  function getAssetNames(assetsToBeConfigured: ConfigurableAsset[]): string {
    let assetNames: string = "";
    if (assetsToBeConfigured.length <= 0) {
      return "";
    } else {
      assetNames = assetsToBeConfigured.map((asset) => asset.name).join(", ");
    }
    return assetNames;
  }

  // Returns a list of asset IDs from the given list of configurable assets.
  function getAssetIds(assetsToBeConfigured: ConfigurableAsset[]): number[] {
    let assetIds: number[] = [];
    if (assetsToBeConfigured.length <= 0) {
      return [];
    } else {
      assetIds = assetsToBeConfigured.map((asset) => asset.assetId);
    }
    return assetIds;
  }

  // Close the confirmation modal.
  function closeModal(): void {
    setSelectedId(-1);
    setSelectedType("");
    setErrorMessage("");
    setAssetsToBeConfigured([]);
  }

  // Update configuration for the selected assets.
  async function updateConfiguration(): Promise<void> {
    let requestBody = {};
    if (selectedId > 0 && selectedType === "assetgroup") {
      const assetIds = getAssetIds(assetsToBeConfigured);
      requestBody = { assetIds: assetIds };
    } else if (selectedId > 0 && selectedType === "asset") {
      requestBody = { assetIds: [selectedId] };
    }
    setLoading(true);
    void apiRequest(`${API}/asset/bulk-configuration`, "POST", requestBody);
    // Delay for just a moment so that it is clear to super admins that the request was sent
    // (having no loading time might be jarring).
    await delayMilliseconds(1000);
    setLoading(false);

    // Due to the long runtime of this lambda to prevent overloading the configuration server, we need to always
    // simply acknowledge that the request was made and move on, instead of waiting to receive a response.
    setErrorMessage("");
    closeModal();
  }

  return failedToLoad ? (
    <Error500Page />
  ) : (
    <div className={styles.body}>
      <div className={`${styles.header} p-4`}>
        <Spinner loading={loading || assetgroupAssetsLoading} />
        <div className="row align-items-top">
          <div className={`${styles.title} mb-4 col`}>
            <span className="align-top">Select Company</span>
          </div>

          {/* Input for selecting company. */}
          <div className="input-group rounded">
            <input
              data-test="company-name-input"
              className="form-control rounded"
              type="search"
              list="company-list"
              placeholder="Select company..."
              value={selectedCompanyName}
              onChange={(e) => {
                setSelectedCompanyName(e.target.value), setCompanyId(getCompanyIdByName(e.target.value));
              }}
            />
            <datalist id="company-list">
              {props.companies.map((company) => (
                <option key={company.companyId} value={company.name} />
              ))}
            </datalist>
          </div>
        </div>
      </div>

      <div>
        <div className="py-4 row">
          {/* Asset group management. */}
          <div className={`${styles.resourceList} col-12 col-md mx-0`} data-test="assetgroup-list">
            <ResourceList
              resourceNameSingular="Asset Group"
              resourceNamePlural="Asset Groups"
              resourceArticle="an"
              headerButtonUserPermissions={[]}
              resourceIdKey="assetgroupId"
              resourcePriorityKey="isDefault"
              resources={sortAssetgroups(assetgroups)}
              resourceRow={AssetgroupListRow}
              helpModal={<HelpAssetgroup />}
              filterKeys={["name"]}
              loading={loading}
              onClickHeaderButton={() => {
                /* Do nothing. */
              }}
              onSelect={(selectedId) => updateSelection(selectedId, "assetgroup")}
            />

            {assetsToBeConfigured.length > 0 ? (
              <ConfirmModal
                showModal={selectedId > 0 && selectedType === "assetgroup"}
                title="Update Configuration?"
                content={`Are you sure you want to update the configuration for all assets in the asset group '${getAssetgroupName(
                  selectedId
                )}'? The following assets will be configured: ${getAssetNames(assetsToBeConfigured)}.`}
                errorMessage={errorMessage}
                yesText="Update Configuration"
                noText="Cancel"
                danger={true}
                onClose={() => closeModal()}
                onYes={() => updateConfiguration()}
                onNo={() => closeModal()}
              />
            ) : (
              <ConfirmModal
                showModal={selectedId > 0 && selectedType === "assetgroup"}
                title="Update Configuration?"
                content={`The assetgroup '${getAssetgroupName(selectedId)}' does not contain any assets to configure.`}
                errorMessage={errorMessage}
                noText="Close"
                danger={true}
                onClose={() => closeModal()}
                onYes={() => {
                  /* Do nothing. */
                }}
                onNo={() => closeModal()}
              />
            )}
          </div>

          {/* Asset management. */}
          <div data-test="asset-list" className={`${styles.resourceList} col-12 col-md mx-0`}>
            <ResourceList
              resourceNameSingular="Asset"
              resourceNamePlural="Assets"
              resourceArticle="an"
              headerButtonUserPermissions={[]}
              resourceIdKey="assetId"
              resources={sortAssets(assets)}
              resourceRow={AssetListRow}
              helpModal={<HelpAsset />}
              filterKeys={["name"]}
              loading={loading}
              onClickHeaderButton={() => {
                /* Do nothing. */
              }}
              onSelect={(selectedId) => updateSelection(selectedId, "asset")}
            />

            <ConfirmModal
              showModal={selectedId > 0 && selectedType === "asset"}
              title="Update Configuration?"
              content={`Are you sure you want to update the configuration for the asset '${getAssetNameById(
                selectedId
              )}'?`}
              errorMessage={errorMessage}
              yesText="Update Configuration"
              noText="Cancel"
              danger={true}
              onClose={() => closeModal()}
              onYes={() => updateConfiguration()}
              onNo={() => closeModal()}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  companies: Company[];
}

interface Company {
  companyId: number;
  name: string;
  code: string;
  isPackager: boolean;
}

interface ResponseBody {
  assetgroups: Assetgroup[];
  assets: Asset[];
}

interface SelectedForConfigurationResponseBody {
  assets: ConfigurableAsset[];
}

interface Assetgroup {
  assetgroupId: number;
  name: string;
  geofenceEnabled: boolean;
  geofenceType: string;
  assets: Asset[];
  assetgroups: Assetgroup[];
  isDefault: boolean;
}

interface Asset {
  assetId: number;
  name: string;
  nickname: string;
  isRented?: boolean;
  isMigrating: boolean;
  lastConfigSuccessful?: boolean;
}

interface ConfigurableAsset {
  assetId: number;
  name: string;
  nickname: string;
  isRental: boolean;
  isMigrating: boolean;
}

interface AssetListItem {
  assetId: number;
  name: string;
  nickname: string;
}

type SelectedType = "" | "assetgroup" | "asset";
